import React from 'react'
import Button from '../../ui-components/Button'
import { getMutualFundDetailCTA, getNumberInParantheses } from '../../../utils/mutualFundDetail'
import { postCTA } from '../../../utils/appBridge'

interface Fund {
  isin: string
  fundName: string
  powerRank: number
}

interface PeerListProps {
  powerRank: number
  isinToBeHighlighted: string
  data: Fund[]
  isRenderAllRows?: boolean
  onViewAll?: (() => void) | null
  isError?: boolean
}

const ElitePowerRankLeaderboard: React.FC<PeerListProps> = ({
  powerRank,
  isinToBeHighlighted,
  data,
  isRenderAllRows,
  onViewAll,
  isError
}) => {
  const renderRows = () => {
    if (powerRank <= 6) {
      return (
        <>
          {data.slice(0, 6).map((fund, index) => (
            <Row
              key={index}
              fund={fund}
              highlight={fund.powerRank === powerRank && fund.isin === isinToBeHighlighted}
              isError={isError}
            />
          ))}
        </>
      )
    } else if (powerRank >= data.length - 2) {
      return (
        <>
          {data.slice(0, 3).map((fund, index) => (
            <Row key={index} fund={fund} isError={isError} />
          ))}
          <div className='px-3 pb-[10px]'>
            <svg width='3' height='15' viewBox='0 0 3 15' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <circle cx='1.5' cy='1.5' r='1.5' fill='#D1D6D6' />
              <circle cx='1.5' cy='7.5' r='1.5' fill='#D1D6D6' />
              <circle cx='1.5' cy='13.5' r='1.5' fill='#D1D6D6' />
            </svg>
          </div>
          {data.slice(-3).map((fund, index) => (
            <Row
              key={index + powerRank}
              fund={fund}
              highlight={fund.powerRank === powerRank && fund.isin === isinToBeHighlighted}
              isError={isError}
            />
          ))}
        </>
      )
    } else {
      return (
        <>
          {data.slice(0, 3).map((fund, index) => (
            <Row key={index} fund={fund} isError={isError} />
          ))}
          <div className='px-3 pb-3'>
            <svg width='3' height='15' viewBox='0 0 3 15' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <circle cx='1.5' cy='1.5' r='1.5' fill='#D1D6D6' />
              <circle cx='1.5' cy='7.5' r='1.5' fill='#D1D6D6' />
              <circle cx='1.5' cy='13.5' r='1.5' fill='#D1D6D6' />
            </svg>
          </div>
          {data.slice(powerRank - 1, powerRank + 2).map((fund, index) => (
            <Row
              key={index + powerRank}
              fund={fund}
              highlight={fund.powerRank === powerRank && fund.isin === isinToBeHighlighted}
              isError={isError}
            />
          ))}
        </>
      )
    }
  }

  const renderAllRows = () => {
    return (
      <>
        {data.map((fund, index) => (
          <Row key={index} fund={fund} highlight={fund.powerRank === powerRank && fund.isin === isinToBeHighlighted} />
        ))}
      </>
    )
  }

  return (
    <div className='w-full bg-white rounded-lg'>
      <div className='mt-4 relative'>
        <div className='flex justify-between items-center text-grey50 text-sm pb-2'>
          <div className='flex items-center'>
            <span className='h-6 -ml-[1px]'>Fund</span>
          </div>
          <span className='ml-auto h-6'>Power Rank</span>
        </div>

        {isRenderAllRows ? renderAllRows() : renderRows()}

        {/* {isError && (
          <div className='absolute  py-1 px-4 rounded-lg top-[50%] left-[30%] border text-[12px] text-white'>
            Peer List Unavailable
          </div>
        )} */}
      </div>

      {onViewAll && data.length > 6 && (
        <Button buttonClassName='rounded-lg h-[48px] mt-4 text-[14px] font-[500]' isFullWidth onClick={onViewAll}>
          View All {getNumberInParantheses(data?.length ?? 0)}
        </Button>
      )}
    </div>
  )
}

interface RowProps {
  fund: Fund
  highlight?: boolean
  isError?: boolean
}

const Row: React.FC<RowProps> = ({ fund, highlight, isError }) => (
  <div
    onClick={() => {
      if (fund?.isin) {
        postCTA(JSON.stringify(getMutualFundDetailCTA({ isin: fund?.isin ?? '', navigationStyle: 'PUSH' })))
      }
    }}
    className={`relative text-[#8E9393]`}
    style={{
      minHeight: highlight ? '44px' : '20px'
    }}
  >
    <div
      style={{
        width: highlight ? 'calc(100% + 20px)' : '100%',
        marginLeft: highlight ? '-10px' : '0px',
        height: '100%'
      }}
      className={`flex justify-between items-center py-[6px] rounded-[12px] ${highlight ? `border-[2px] border-[#232323] absolute pl-2 pr-2` : ''}`}
    >
      <div className='flex justify-between text-sm w-full'>
        <div className='flex items-center gap-4'>
          <span className={`min-h-6 max-w-60 truncate ${highlight ? ' pt-[1px]' : 'text-[#232323]'}  text-[#232323]`}>
            {fund.fundName}
          </span>
        </div>
        <span className={`ml-auto h-6 ${highlight ? 'text-[#232323] font-[500]' : 'text-[#6A6E6E] font-[400]'}`}>
          {fund.powerRank}
        </span>
      </div>
    </div>
  </div>
)

export default ElitePowerRankLeaderboard
